import store from '@/store'
import { Action, config, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { API, graphqlOperation } from "aws-amplify";
import { GraphQLResult } from "@aws-amplify/api/lib-esm/types";
import {
    CreateCommentInput,
    CreateCommentMutation,
    CreateThreadInput,
    CreateThreadMutation,
    DeleteCommentInput,
    DeleteCommentMutation,
    DeleteThreadInput,
    DeleteThreadMutation,
    GetThreadQuery,
    GetThreadQueryVariables,
    ListThreadsQuery,
    UpdateCommentInput,
    UpdateCommentMutation,
    Thread
} from "@/API";

import { createComment, createThread, deleteComment, deleteThread, updateComment } from "@/graphql/mutations";

import Vue from "vue";
import { Observable } from 'zen-observable-ts';
import { getThread, listThreads } from "@/graphql/queries";
import { onCreateThread } from "@/graphql/subscriptions";
import WorkspaceOptions from './WorkspaceOptions';
import Modelling from './Modelling';
import Choices from './Choices';
import Viewpoints from './Viewpoints';
import Decisions from '@/store/modules/Decisions';
import _ from 'lodash';

config.rawError = true
const modellingModule = getModule(Modelling)
const choicesModule = getModule(Choices)
const workspaceOptions = getModule(WorkspaceOptions)
const viewpointModule = getModule(Viewpoints)
const name = 'comments';
if (module.hot) {
    if (store.hasModule(name)) {
        store.unregisterModule(name);
    }
}
@Module({dynamic: true, store, name: name})
export default class Comments extends VuexModule {

    commentThreads: any = {};
    // Removed for focus comment update CHRIS SEPT 24
    // selectedCommentResource: CommentResource | null = null;
    allComments: { [id: string]: any } = {};


    get selectedCommentResourceId() {
        if (this.newSelectedCommentResource) {
            return Comments.generateId(
                this.newSelectedCommentResource.resource.id,
                this.newSelectedCommentResource.secondaryResource.id,
                this.newSelectedCommentResource.tertiaryResource.id)
        }
    }

  
    @Mutation
    setAllComments(allComments: { [id: string]: any}) {
        this.allComments = allComments;
    }

    @Mutation
    setCommentThreads(commentThreads: any) {
        this.commentThreads = commentThreads;
    }

    @Mutation
    addCommentThread(commentThread: any) {
        Vue.set(this.commentThreads, Comments.generateId(commentThread.resourceId, commentThread.secondaryResourceId, commentThread.tertiaryResourceId), commentThread);
    }

    @Mutation
    removeCommentThread(thread: any) {
        Vue.delete(this.commentThreads, Comments.generateId(thread.resourceId, thread.secondaryResourceId, thread.tertiaryResourceId))
    }

    // Removed for focus comment update CHRIS SEPT 24
    // @Mutation
    // setSelectedCommentResource(selectedResource: CommentResource | null) {
    //     this.selectedCommentResource = selectedResource;
    // }

    @Action
    async fetchComments() {
        this.setCommentThreads({});

        let selectedDecision = getModule(Decisions).selectedDecision
        if (!selectedDecision) return;

        let result = await API.graphql(graphqlOperation(listThreads, {decisionId: selectedDecision.decisionId})) as GraphQLResult
        let data: ListThreadsQuery = result.data as ListThreadsQuery;
        if (data.listThreads && data.listThreads.items) {
            let commentThreads = data.listThreads.items
                .reduce((map: any, obj: any) => (map[Comments.generateId(obj.resourceId, obj.secondaryResourceId, obj.tertiaryResourceId)] = obj, map), {});
            this.setCommentThreads(commentThreads)
        }
        let observable = API.graphql(graphqlOperation(onCreateThread, {decisionId: selectedDecision.decisionId})) as Observable<object>
        observable.subscribe({
            next: (onCreateThread: any) => {
                let newThread = onCreateThread.value.data.onCreateThread
                if (newThread.comments && newThread.comments.items) {
                    newThread.comments = [];
                }
                this.addCommentThread(newThread);
            },
            error: (error: any) => {
                console.log(JSON.stringify(error));
            }
        })
    }

    @Action
    async getCommentThread(payload: GetThreadQueryVariables) {
        let result = await (API.graphql(graphqlOperation(getThread, payload)) as Promise<GraphQLResult>);
        let getResult = result.data as GetThreadQuery
        return getResult.getThread;
    }

    @Action
    async createCommentThread(payload: CreateThreadInput) {
        let res = await API.graphql(graphqlOperation(createThread, {input: payload})) as GraphQLResult;
        let thread = (res.data as CreateThreadMutation).createThread;
        // this.addCommentThread(thread)
        return thread;
    }

    @Action
    async deleteCommentThread(payload: DeleteThreadInput) {
        let res = await API.graphql(graphqlOperation(deleteThread, {input: payload})) as GraphQLResult;
        let thread = (res.data as DeleteThreadMutation).deleteThread;
        this.removeCommentThread(payload)
        return thread;
    }

    @Action
    async commentOnThread(payload: CreateComment) {
        let id = Comments.generateId(payload.resourceId, payload.secondaryResourceId, payload.tertiaryResourceId)
        let commentThread = this.commentThreads[id]
        if (!commentThread) {
            commentThread = (await this.createCommentThread({
                decisionId: payload.decisionId,
                resourceId: payload.resourceId,
                secondaryResourceId: payload.secondaryResourceId,
                tertiaryResourceId: payload.tertiaryResourceId
            }))
        }

        let createCommentInput: CreateCommentInput = {
            comment: payload.comment,
            decisionId: payload.decisionId,
            threadId: commentThread.id,
            tags: payload.tags
        }
        let res: GraphQLResult = await API.graphql(graphqlOperation(createComment, {input: createCommentInput})) as GraphQLResult;
        return (res.data as CreateCommentMutation).createComment
    }

    @Action
    async updateComment(payload: UpdateCommentInput) {
        let updateCommentInput: UpdateCommentInput = {
            id: payload.id,
            threadId: payload.threadId,
            decisionId: payload.decisionId,
            replyingTo: payload.replyingTo,
            comment: payload.comment,
            userId: payload.userId,
            tags: payload.tags
        }
        let res: GraphQLResult = await API.graphql(graphqlOperation(updateComment, { input: updateCommentInput})) as GraphQLResult;
        return (res.data as UpdateCommentMutation).updateComment
    }

    // Deletes a comment that is on a thread.
    // Will delete the thread if there are no more comments
    @Action
    async deleteComment(payload: DeleteCommentInput) {
        let res: GraphQLResult = await API.graphql(graphqlOperation(deleteComment, {input: payload})) as GraphQLResult;
        return (res.data as DeleteCommentMutation).deleteComment;
    }

    // Removed for focus comment update CHRIS SEPT 24
    // @Action
    // async selectCommentResource(commentResource: CommentResource | null) {
    //     this.setSelectedCommentResource(commentResource);
    // }


    static generateId(resourceId: string, secondaryResourceId: string, tertiaryResourceId: string): string {
        return [resourceId, secondaryResourceId, tertiaryResourceId].join('#')
    }

    get newSelectedCommentResource() {
        let decisionModule = getModule(Decisions)
        let selectedFactorId = workspaceOptions.selectedPathItemId ? workspaceOptions.selectedPathItemId : 'M'
        //If factorResources, viewpoint and choicesModule are valid
        if(modellingModule.publishedModelFactorResources && selectedFactorId && selectedFactorId in modellingModule.publishedModelFactorResources && viewpointModule && choicesModule && decisionModule.selectedDecisionId && viewpointModule.decisionViewpoints != null) {
            if (viewpointModule.activeViewpointId in viewpointModule.decisionViewpointsMap && choicesModule.selectedChoice) {
                return {
                    decisionId: decisionModule.selectedDecisionId,
                    resource:{
                        name: viewpointModule.decisionViewpointsMap[viewpointModule.activeViewpointId].name,
                        id: viewpointModule.activeViewpointId,
                        type: ResourceType.VIEWPOINT
                    },
                    secondaryResource: {
                        name: choicesModule.selectedChoice?.name,
                        id: choicesModule.selectedChoiceId,
                        type: ResourceType.CHOICE
                    },
                    tertiaryResource:{
                        name: modellingModule.publishedModelFactorResources[selectedFactorId].name,
                        id: selectedFactorId,
                        type: ResourceType.FACTOR
                    }
                }
            }
            else if(choicesModule.selectedChoice) {
                return {
                    name: 'DecisionId',
                    decisionId: decisionModule.selectedDecisionId,
                    type: ResourceType.CHOICE,

                    resource: {
                        name: 'Choice',
                        id: 'Choice',
                        type: ResourceType.CHOICE
                    },
                    secondaryResource: {
                        id: choicesModule.selectedChoiceId,
                        type: ResourceType.CHOICE,
                        name: getModule(Choices).choices[choicesModule.selectedChoiceId]?.name
                    },
                    tertiaryResource: {
                        id: selectedFactorId,
                        type: ResourceType.FACTOR,
                        name: modellingModule.publishedModelFactorResources[selectedFactorId]?.name
                    }
                }
            }
            else {
                return {
                    name: 'DecisionId',
                    decisionId: decisionModule.selectedDecisionId,
                    type: ResourceType.FACTOR,

                    resource: {
                        name: 'MODEL',
                        id: 'MODEL',
                        type: ResourceType.FACTOR
                    },
                    secondaryResource: {
                        id: 'FACTOR',
                        type: ResourceType.FACTOR,
                        name: 'FACTOR'
                    },
                    tertiaryResource: {
                        id: selectedFactorId,
                        type: ResourceType.FACTOR,
                        name: modellingModule.publishedModelFactorResources[selectedFactorId]?.name
                    }
                }
            }
        } else{
            return null
        }
        
    }   

    @Action 
    async cloneComments(payload: {newDecisionId: string, oldDecisionId: string, map: any, includeChoices: boolean, includeViewpoints: boolean}) {
        let commentResourceConstants = ['Choice', 'MODEL', 'FACTOR'];
        await this.fetchComments();
        //Loop through comment threads and make copies in new decision
        for(let commentThread of Object.values(this.commentThreads)) {
            let commentResource = commentThread as Thread;
            //If choices not included, skip all comments with resourceId Choice
            if(!payload.includeChoices) {
                if (commentResource.resourceId === 'Choice') {
                    continue;
                }
            }
            //If Viewpoints not included, skip all comments with resourceId of an id (ViewpointId)
            if(!payload.includeViewpoints) {
                if (commentResource.resourceId in payload.map) {
                    continue;
                }
            }

            //If Viewpoints included but choices aren't then skip any viewpoint commment
            if(payload.includeViewpoints && !payload.includeChoices) {
                if (commentResource.resourceId in payload.map) {
                    continue;
                }
            }
            let newCommmentThread: CreateThreadInput = {
                resourceId: commentResourceConstants.includes(commentResource.resourceId) ? commentResource.resourceId : payload.map[commentResource.resourceId],
                secondaryResourceId: commentResourceConstants.includes(commentResource.secondaryResourceId) ? commentResource.secondaryResourceId : payload.map[commentResource.secondaryResourceId],
                tertiaryResourceId: commentResourceConstants.includes(commentResource.tertiaryResourceId) ? commentResource.tertiaryResourceId : commentResource.tertiaryResourceId,
                decisionId: payload.newDecisionId
            }

            if(newCommmentThread.resourceId && newCommmentThread.secondaryResourceId && newCommmentThread.tertiaryResourceId) {
                let result = await this.createCommentThread(newCommmentThread);
                //For each thread loop through the comments in the thread and create a copy in the new thread 
                for(let comment of commentResource.comments.items || []) {
                    if(comment) {
                        let newComment: CreateCommentInput = {
                            threadId: result.id,
                            comment: comment.comment,
                            tags: comment.tags,
                            decisionId: payload.newDecisionId
                        }
                        let res: GraphQLResult = await API.graphql(graphqlOperation(createComment, { input: newComment })) as GraphQLResult;
                    }
                }
            }
        }

    }
}

interface CreateComment extends CreateCommentInput {
    decisionId: string,
    resourceId: string,
    secondaryResourceId: string,
    tertiaryResourceId: string
}

interface CommentResource {
    name?: string,
    decisionId: string,
    resource: {
        id: string | 'CONTENT',
        name?: string,
        type: ResourceType
    },
    secondaryResource: {
        id: string | "WEIGHT",
        name?: string,
        type: ResourceType
    },
    tertiaryResource: {
        id: string,
        name?: string,
        type: ResourceType
    },
    tags?: string[]

}

enum ResourceType {
    FACTOR = 'FACTOR',
    CHOICE = 'CHOICE',
    VIEWPOINT = 'VIEWPOINT',
}

export {
    CommentResource,
    ResourceType
}

